import React, { useState, useCallback } from "react";
import { X, UserPlus } from "lucide-react";
import type { CreateAccountData, UserAccountResponse } from "@/types";
import { CopyCredentials } from "./CopyCredentials";

interface CreateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateAccount: (
    accountData: CreateAccountData
  ) => Promise<UserAccountResponse | null>;
}

export const CreateAccountModal: React.FC<CreateAccountModalProps> = ({
  isOpen,
  onClose,
  onCreateAccount,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [accountResponse, setAccountResponse] =
    useState<UserAccountResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateAccountData>({
    name: "",
    email: "",
    role: "",
  });

  const handleInputChange = useCallback(
    (field: keyof CreateAccountData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setError(null);
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      setError("Name and email are required fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await onCreateAccount(formData);

      if (response) {
        setAccountResponse(response);
      } else {
        setError("Failed to create account. Please try again.");
      }
    } catch (err) {
      console.error("Account creation error:", err);
      setError("An unexpected error occurred while creating account.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setAccountResponse(null);
    setError(null);
    setFormData({ name: "", email: "", role: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content create-account-modal">
        <div className="modal-header">
          <h2>Create New Account</h2>
          <button
            onClick={handleClose}
            className="close-button"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {!accountResponse ? (
            <form onSubmit={handleSubmit} className="create-account-form">
              <div className="form-group">
                <label htmlFor="user-name">Full Name *</label>
                <input
                  id="user-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter full name"
                  required
                  disabled={isCreating}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="user-email">Email Address *</label>
                <input
                  id="user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email address"
                  required
                  disabled={isCreating}
                  className="form-input"
                />
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-info">
                <p>
                  <strong>Note:</strong> A system-generated password will be
                  created. The user will be required to change it on first
                  login.
                </p>
              </div>
            </form>
          ) : (
            <div className="creation-success">
              <div className="success-header">
                <div className="success-icon">✅</div>
                <h3>Account Created Successfully!</h3>
                <p>
                  New account has been created for {accountResponse.user_name}
                </p>
              </div>

              <CopyCredentials
                credentials={{
                  email: accountResponse.user_email,
                  password: accountResponse.password,
                }}
                title="Account Credentials"
                className="creation-credentials"
              />

              <div className="creation-instructions">
                <h4>Next Steps:</h4>
                <ol>
                  <li>Copy the credentials using the button above</li>
                  <li>Share the login details with the user securely</li>
                  <li>
                    Inform the user to change their password on first login
                  </li>
                  <li>
                    Email: <code>{accountResponse.user_email}</code>
                  </li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!accountResponse ? (
            <div className="form-actions">
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={
                  isCreating || !formData.name.trim() || !formData.email.trim()
                }
                className="create-button"
              >
                <UserPlus size={16} />
                {isCreating ? "Creating..." : "Create Account"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                disabled={isCreating}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="success-actions">
              <button onClick={handleClose} className="done-button">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
